import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, Label, Tooltip, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import { Users, FileText, Info, Download, Search, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown, X, TrendingUp, TrendingDown, MapPin, Map, Grid3X3, Target, Crosshair, IndianRupee, ArrowRightLeft, Clock, Landmark, Wallet, Scale, ArrowUpRight } from "lucide-react";
import IndiaMap from "../components/IndiaMap";
import SankeyChart from "../components/SankeyChart";
import ThemeChip from "../components/ThemeChip";
import { getThemeDotColor } from "../utils/dynamicColors";
import { fmtCr, fmtCrNoSymbol, fmtNum, displayTheme } from "../utils/format";
import { useCommandCentreData } from "../hooks/useCommandCentreData";

const PIE_COLORS = ["#006837", "#16A34A", "#F59E0B", "#64748B", "#8B5CF6", "#0EA5E9", "#D97706", "#059669"];

/* ─── YoY Delta Badge ───────────────────────────────────────────── */
function YoyBadge({ delta, prevFy }) {
  if (!delta || delta.pct === null || delta.pct === undefined) return null;
  const pct = delta.pct;
  const isUp = pct > 0;
  const isDown = pct < 0;
  const isFlat = pct === 0;
  const Icon = isUp ? TrendingUp : isDown ? TrendingDown : null;
  const color = isUp ? "text-green-600" : isDown ? "text-red-500" : "text-slate-600";
  const bg = isUp ? "bg-green-50" : isDown ? "bg-red-50" : "bg-slate-50";
  const label = `${isUp ? "+" : ""}${pct.toFixed(1)}%`;
  return (
    <div className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full ${bg} ${color}`} data-testid="yoy-badge" title={`vs ${prevFy || "last FY"}`}>
      {Icon && <Icon size={10} strokeWidth={2} />}
      <span className="text-[9px] font-semibold leading-none">{isFlat ? "0%" : label}</span>
    </div>
  );
}

export default function CommandCentre() {
  const navigate = useNavigate();
  const {
    summary, geoSummary, stateData, allocationByTheme, disbTrend,
    ragSummary, loading, sf, s, hasGrants, g, totalSanctioned,
    selectedState, selectedDistrict, districtData, blockData, mapLoading,
    handleStateSelect, handleDistrictSelect,
    regionSummary, regionGeoSummary, regionRagSummary,
    aspirationalData, trustMatrix, awpMatrix, heatmapToggle, setHeatmapToggle,
    sankeyData,
    quarterlyData,
    mapViewMode, setMapViewMode, allDistrictData,
    yoyData,
    grants, grantsLoading, grantsPage, setGrantsPage, grantsTotal,
    grantsTotalPages, grantsSearchInput, setGrantsSearchInput,
    grantsSortBy, grantsSortOrder, grantsExporting,
    handleGrantsSort, handleGrantsSearch, clearGrantsSearch,
    handleGrantsExport, scrollToGrantsTable, grantsTableRef,
    grantsTableOpen, setGrantsTableOpen,
    filters, filterOptions, viewMode, API,
  } = useCommandCentreData();

  const statusClass = (s) =>
    s === "Active" || s === "Ongoing" || s === "Under Closure" ? "bg-green-100 text-green-700" :
    s === "Closed" ? "bg-slate-100 text-slate-500" :
    "bg-amber-100 text-amber-700";

  // Only show YoY badges when a specific FY is selected
  const yoy = filters.fy ? yoyData : null;

  // Toggle between YoY and QoQ chart views
  const [chartView, setChartView] = useState("yoy");

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "–";

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#006837]" />
      </div>
    );
  }

  return (
    <div data-testid="command-centre" className="space-y-3">
      {/* Main Content: Map + Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-3 items-stretch">

        {/* LEFT: Map Panel */}
        <div className="min-w-0 overflow-hidden">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-3 overflow-hidden flex justify-center">
              {sf ? (
                <IndiaMap
                  stateData={stateData}
                  districtData={districtData}
                  blockData={blockData}
                  allDistrictData={allDistrictData}
                  onStateSelect={handleStateSelect}
                  onDistrictSelect={handleDistrictSelect}
                  selectedState={selectedState}
                  selectedDistrict={selectedDistrict}
                  dataLoading={mapLoading}
                  mapViewMode={mapViewMode}
                  onMapViewChange={(mode) => { setMapViewMode(mode); if (selectedState) handleStateSelect(null); }}
                />
              ) : (
                <div className="h-64 flex items-center justify-center bg-slate-50 rounded-lg text-slate-600">
                  Map requires data connection
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: Metrics Panel (1/3 width) — compact */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-3 flex flex-col gap-2">

            {/* Grants Summary */}
            <div className="bg-slate-50 rounded-lg p-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="font-semibold text-slate-700 text-xs uppercase tracking-wide">Grants</h3>
                <button onClick={scrollToGrantsTable} className="text-[10px] text-[#006837] hover:underline" data-testid="grants-view-scroll">View →</button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div
                  onClick={scrollToGrantsTable}
                  className="flex items-center gap-2 p-2.5 bg-white rounded-lg border border-slate-100 cursor-pointer hover:border-[#006837]/30 transition-colors"
                  data-testid="kpi-grants-approved"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#006837] flex items-center justify-center flex-shrink-0">
                    <FileText size={18} className="text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xl font-bold text-slate-900 leading-none">{fmtNum(s?.total_grants || s?.grants)}</span>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{viewMode === "snapshot" ? "New Grants" : viewMode === "active" ? "All Grants" : "Ongoing Grants"}</div>
                  </div>
                </div>
                <div
                  onClick={scrollToGrantsTable}
                  className="flex items-center gap-2 p-2.5 bg-white rounded-lg border border-slate-100 cursor-pointer hover:border-green-300 transition-colors"
                  data-testid="kpi-grantees"
                >
                  <div className="w-9 h-9 rounded-lg bg-green-600 flex items-center justify-center flex-shrink-0">
                    <Users size={18} className="text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xl font-bold text-slate-900 leading-none">{fmtNum(s?.active_grantees || s?.grantees)}</span>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Grantees</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Summary */}
            <div className="bg-slate-50 rounded-lg p-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="font-semibold text-slate-700 text-xs uppercase tracking-wide">Financial</h3>
                {s?.is_apportioned && (
                  <div className="group relative">
                    <Info size={13} className="text-amber-500 cursor-help" data-testid="apportionment-info-icon" />
                    <div className="absolute right-0 top-5 z-50 hidden group-hover:block w-64 p-2.5 bg-slate-800 text-white text-[10px] leading-relaxed rounded-lg shadow-lg">
                      Amounts are proportionally estimated. Multi-state grants are split by block/district count in this region.
                    </div>
                  </div>
                )}
              </div>
              {(() => {
                const isOnlyFy2425 = Array.isArray(filters.fy) && filters.fy.length === 1 && filters.fy[0] === "FY 2024-25";
                return (
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-gradient-to-br from-blue-50 to-white rounded-lg p-2 border border-blue-100 flex flex-col justify-end" data-testid="kpi-sanctioned">
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-5 h-5 rounded flex items-center justify-center bg-blue-100"><IndianRupee size={11} className="text-blue-600" /></div>
                    <span className="text-[10px] text-blue-700 font-medium">Sanctioned {viewMode === "snapshot" ? "(New)" : viewMode === "ongoing" ? "(Ongoing)" : "(New + Ongoing)"}</span>
                    {s?.is_apportioned && <span className="text-[8px] text-amber-500 font-medium">~est.</span>}
                  </div>
                  <span className="text-lg font-bold text-slate-900">{fmtCr(s?.total_allocation_cr || s?.allocation_cr)}</span>
                </div>
                <div className="bg-gradient-to-br from-emerald-50 to-white rounded-lg p-2 border border-emerald-100 flex flex-col justify-end" data-testid="kpi-cumulative-disbursement">
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-5 h-5 rounded flex items-center justify-center bg-emerald-100"><ArrowRightLeft size={11} className="text-emerald-600" /></div>
                    <span className="text-[10px] text-emerald-700 font-medium">Disbursement (Cumulative) till FY selected</span>
                  </div>
                  <span className="text-lg font-bold text-slate-900">{fmtCr(s?.cumulative_disbursement_cr)}</span>
                </div>
                <div className="bg-gradient-to-br from-violet-50 to-white rounded-lg p-2 border border-violet-100 flex flex-col justify-end" data-testid="kpi-disbursed">
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-5 h-5 rounded flex items-center justify-center bg-violet-100"><Wallet size={11} className="text-violet-600" /></div>
                    <span className="text-[10px] text-violet-700 font-medium">Disbursed {viewMode === "snapshot" ? "(New)" : viewMode === "ongoing" ? "(Ongoing)" : "(New + Ongoing)"} for FY selected</span>
                    {s?.is_apportioned && <span className="text-[8px] text-amber-500 font-medium">~est.</span>}
                  </div>
                  <span className="text-lg font-bold text-slate-900">{fmtCr(s?.total_disbursement_cr || s?.disbursement_cr)}</span>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-white rounded-lg p-2 border border-amber-100 flex flex-col justify-end relative group" data-testid="kpi-pending-disbursement">
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-5 h-5 rounded flex items-center justify-center bg-amber-100"><Clock size={11} className="text-amber-600" /></div>
                    <span className="text-[10px] text-amber-700 font-medium">Pending Disbursement</span>
                    <Info size={10} className="text-amber-400 cursor-help" />
                  </div>
                  <span className="text-lg font-bold text-slate-900">{fmtCr(((s?.total_allocation_cr || s?.allocation_cr) || 0) - (s?.cumulative_disbursement_cr || 0))}</span>
                  <div className="absolute right-0 top-full mt-1 hidden group-hover:block bg-slate-800 text-white text-[10px] rounded-md px-3 py-2 shadow-lg z-40 whitespace-nowrap pointer-events-none">
                    <span className="font-bold text-[11px] block mb-1">Pending Disbursement</span>
                    <span className="block">= Sanctioned − Disbursement (Cumulative)</span>
                    <span className="block mt-1">= {fmtCr(s?.total_allocation_cr || s?.allocation_cr)} − {fmtCr(s?.cumulative_disbursement_cr)}</span>
                    <span className="block font-bold mt-1">= {fmtCr(((s?.total_allocation_cr || s?.allocation_cr) || 0) - (s?.cumulative_disbursement_cr || 0))}</span>
                  </div>
                </div>
              </div>
                );
              })()}
            </div>

            {/* Geographic Stats */}
            <div className="bg-slate-50 rounded-lg p-2.5" data-testid="geographic-card">
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="font-semibold text-slate-700 text-xs uppercase tracking-wide">Geographic</h3>
                <button onClick={() => navigate('/geographic')} className="text-[10px] text-[#006837] hover:underline">Map →</button>
              </div>
              <div className="space-y-1.5">
                <div className="grid grid-cols-2 gap-1.5">
                  <div className="bg-white rounded-lg p-2 border border-slate-100 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <MapPin size={14} className="text-blue-500" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-slate-800 leading-tight">{fmtNum(g?.states) || '–'}</div>
                      <div className="text-[10px] text-slate-500">States & UT</div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-2 border border-slate-100 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0">
                      <Map size={14} className="text-teal-500" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-slate-800 leading-tight">{fmtNum(g?.districts) || '–'}</div>
                      <div className="text-[10px] text-slate-500">Districts</div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <div className="bg-white rounded-lg p-2 border border-slate-100 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Grid3X3 size={14} className="text-slate-500" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-slate-800 leading-tight">{fmtNum(g?.blocks) || '–'}</div>
                      <div className="text-[10px] text-slate-500">Blocks</div>
                    </div>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-2 border border-amber-200 flex items-center gap-2" data-testid="asp-districts-card">
                    <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Target size={14} className="text-amber-600" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-amber-700 leading-tight">{aspirationalData.ad?.covered || 0}/{aspirationalData.ad?.total || 0}</div>
                      <div className="text-[10px] text-slate-500">Asp. Districts</div>
                    </div>
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-2 border border-purple-200 flex items-center gap-2 justify-center" data-testid="asp-blocks-card">
                  <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Crosshair size={14} className="text-purple-600" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-purple-700 leading-tight">{aspirationalData.ab?.covered || 0}/{aspirationalData.ab?.total || 0}</div>
                    <div className="text-[10px] text-slate-500">Aspirational Blocks</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* BELOW-THE-FOLD: YoY chart + Source of Fund × Portfolio side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">

        {/* 1. YoY / QoQ Toggle Chart */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col" data-testid="disb-util-trend-section">
          <div className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">
                {chartView === "yoy" ? "YoY FY-wise Sanctioned & Disbursement" : "Quarterly Sanctioned & Disbursement"}
                {selectedState && <span className="text-[#006837]"> — {selectedDistrict || selectedState}</span>}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {chartView === "yoy" ? "Annual sanctioned with disbursement overlay" : "Quarter-wise trend across all FYs"}
                {selectedState && <span> (filtered by geography)</span>}
              </p>
            </div>
            <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="yoy-qoq-toggle">
              <button onClick={() => setChartView("yoy")}
                className={`px-3 py-1.5 text-[11px] font-semibold transition-colors ${chartView === "yoy" ? "bg-[#006837] text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
                data-testid="toggle-yoy">YoY</button>
              <button onClick={() => setChartView("qoq")}
                className={`px-3 py-1.5 text-[11px] font-semibold transition-colors ${chartView === "qoq" ? "bg-[#006837] text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
                data-testid="toggle-qoq">QoQ</button>
            </div>
          </div>
          <div className="p-4">
            {chartView === "yoy" ? (
              /* ── YoY View ── */
              hasGrants && disbTrend.length > 0 ? (() => {
              const maxVal = Math.max(...disbTrend.map(d => d.allocation_cr || 0), 1);
              // Key changes on data to force re-mount and replay animations
              const animKey = disbTrend.map(d => `${d.fy}-${d.allocation_cr}-${d.disbursement_cr}`).join("|");
              return (
                <div className="space-y-5" key={animKey}>
                  {/* Stacked horizontal bars */}
                  <div className="space-y-4">
                    {disbTrend.map((d, i) => {
                      const sanctPct = Math.max((d.allocation_cr / maxVal) * 100, 0);
                      const disbPct = d.allocation_cr > 0 ? (d.disbursement_cr / d.allocation_cr) * 100 : 0;
                      const disbRate = d.allocation_cr > 0 ? ((d.disbursement_cr / d.allocation_cr) * 100).toFixed(1) : "0.0";
                      const rateNum = parseFloat(disbRate);
                      const rateBg = rateNum >= 50 ? "bg-green-100 text-green-700" : rateNum >= 25 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";
                      // When disbursed bar is too narrow (< 8% of sanctioned), label won't fit inside
                      const disbBarTooNarrow = disbPct < 8;
                      return (
                        <div key={d.fy} className="yoy-row-enter" style={{ animationDelay: `${i * 0.12}s` }} data-testid={`yoy-bar-${d.fy}`}>
                          <div className="flex items-center gap-3 group cursor-default">
                            <span className="text-[11px] font-bold text-slate-700 w-20 flex-shrink-0 tracking-tight">{d.fy}</span>
                            <div className="flex-1 relative h-10 rounded-lg overflow-hidden bg-slate-50 shadow-inner transition-shadow duration-300 group-hover:shadow-md">
                              {/* Sanctioned (full bar) — gradient */}
                              <div className="yoy-bar-sanctioned absolute inset-y-0 left-0 rounded-lg"
                                style={{
                                  width: `${sanctPct}%`,
                                  background: "linear-gradient(90deg, #E2E8F0 0%, #CBD5E1 60%, #B0BEC5 100%)",
                                  animationDelay: `${i * 0.12 + 0.15}s`,
                                }} />
                              {/* Disbursed (overlay) — gradient with sheen */}
                              <div className="yoy-bar-disbursed absolute inset-y-0 left-0 rounded-l-lg"
                                style={{
                                  width: `${sanctPct * (disbPct / 100)}%`,
                                  background: "linear-gradient(90deg, #004529 0%, #006837 50%, #238443 100%)",
                                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.15)",
                                  animationDelay: `${i * 0.12 + 0.3}s`,
                                }} />
                              {/* Value labels inside bar */}
                              <div className="absolute inset-0 flex items-center px-3 pointer-events-none">
                                {d.disbursement_cr > 0 && !disbBarTooNarrow && (
                                  <span className="text-[10px] font-bold text-white drop-shadow z-10" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}>
                                    {`₹${d.disbursement_cr.toFixed(1)} Cr`}
                                  </span>
                                )}
                                {d.disbursement_cr > 0 && disbBarTooNarrow && (
                                  <span className="text-[10px] font-bold text-[#006837] z-10"
                                    style={{ marginLeft: `${Math.max(sanctPct * (disbPct / 100), 2)}%`, paddingLeft: 6 }}>
                                    {`₹${d.disbursement_cr.toFixed(1)} Cr`}
                                  </span>
                                )}
                                <span className="text-[10px] font-semibold text-slate-700 z-10 ml-auto">
                                  ₹{d.allocation_cr.toFixed(1)} Cr
                                </span>
                              </div>
                            </div>
                            {/* Disbursement Rate Pill */}
                            <span className={`text-[10px] font-bold px-2 py-1 rounded-full w-14 text-center flex-shrink-0 ${rateBg}`}>
                              {disbRate}%
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Legend */}
                  <div className="flex items-center gap-4 text-[10px] text-slate-600 pt-2 border-t border-slate-100">
                    <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm inline-block" style={{ background: "linear-gradient(90deg, #E2E8F0, #B0BEC5)" }} /> Sanctioned {viewMode === "snapshot" ? "(New)" : viewMode === "ongoing" ? "(Ongoing)" : "(New + Ongoing)"}</span>
                    <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm inline-block" style={{ background: "linear-gradient(90deg, #004529, #238443)" }} /> Disbursed {viewMode === "snapshot" ? "(New)" : viewMode === "ongoing" ? "(Ongoing)" : "(New + Ongoing)"}</span>
                    <span className="ml-auto text-slate-600">% = Disbursement Rate</span>
                  </div>
                  {/* Summary table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse" data-testid="yoy-summary-table">
                      <thead>
                        <tr className="border-b border-slate-100">
                          <th className="text-left text-[10px] font-medium text-slate-600 uppercase py-1.5 pr-3">FY</th>
                          <th className="text-right text-[10px] font-medium text-slate-600 uppercase py-1.5 px-3">Sanctioned {viewMode === "snapshot" ? "(New)" : viewMode === "ongoing" ? "(Ongoing)" : "(New + Ongoing)"}</th>
                          <th className="text-right text-[10px] font-medium text-slate-600 uppercase py-1.5 px-3">Disbursed {viewMode === "snapshot" ? "(New)" : viewMode === "ongoing" ? "(Ongoing)" : "(New + Ongoing)"}</th>
                          <th className="text-right text-[10px] font-medium text-slate-600 uppercase py-1.5 px-3">Disb Rate</th>
                          {disbTrend.length > 1 && <th className="text-right text-[10px] font-medium text-slate-600 uppercase py-1.5 pl-3">YoY Disbursement Growth</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {disbTrend.map((d, i) => {
                          const disbRate = d.allocation_cr > 0 ? ((d.disbursement_cr / d.allocation_cr) * 100).toFixed(1) : "0.0";
                          const prevDisb = i > 0 ? disbTrend[i - 1].disbursement_cr : null;
                          const yoyGrowth = prevDisb && prevDisb > 0 ? (((d.disbursement_cr - prevDisb) / prevDisb) * 100).toFixed(1) : null;
                          return (
                            <tr key={d.fy} className="border-b border-slate-50 hover:bg-slate-50/50">
                              <td className="py-1.5 pr-3 font-semibold text-slate-700 whitespace-nowrap">{d.fy}</td>
                              <td className="py-1.5 px-3 text-right font-medium text-slate-800">₹{d.allocation_cr.toFixed(2)} Cr</td>
                              <td className="py-1.5 px-3 text-right font-medium text-slate-800">₹{d.disbursement_cr.toFixed(2)} Cr</td>
                              <td className="py-1.5 px-3 text-right">
                                <span className={`font-bold px-1.5 py-0.5 rounded-full text-[10px] ${
                                  parseFloat(disbRate) >= 50 ? "bg-green-100 text-green-700" : parseFloat(disbRate) >= 25 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"
                                }`}>{disbRate}%</span>
                              </td>
                              {disbTrend.length > 1 && (
                                <td className="py-1.5 pl-3 text-right font-medium">
                                  {yoyGrowth !== null ? (
                                    <span className={parseFloat(yoyGrowth) >= 0 ? "text-green-600" : "text-red-600"}>
                                      {parseFloat(yoyGrowth) > 0 ? "+" : ""}{yoyGrowth}%
                                    </span>
                                  ) : <span className="text-slate-500">—</span>}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })() : <div className="py-8 text-center text-sm text-slate-600">{loading ? "Loading..." : "No data for selected filters"}</div>
            ) : (
              /* ── QoQ View ── */
              quarterlyData.length > 0 ? (
                <div data-testid="qoq-chart">
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={quarterlyData} margin={{ top: 10, right: 20, left: 10, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#64748b" }} angle={-45} textAnchor="end" interval={0} height={60} />
                      <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(v) => `${v}`} width={50}
                        label={{ value: "Amount (Cr)", angle: -90, position: "insideLeft", style: { fontSize: 10, fill: "#94a3b8" }, offset: 0 }} />
                      <Tooltip
                        contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e2e8f0" }}
                        formatter={(value, name) => [`₹${value} Cr`, name === "sanctioned_cr" ? "Sanctioned" : "Disbursed"]}
                        labelStyle={{ fontWeight: 600, color: "#334155" }}
                      />
                      <Legend verticalAlign="top" height={30} iconType="line"
                        formatter={(value) => <span className="text-xs text-slate-600">{value === "sanctioned_cr" ? "Sanctioned" : "Disbursed"}</span>} />
                      <Line type="monotone" dataKey="sanctioned_cr" name="sanctioned_cr" stroke="#006837" strokeWidth={2} dot={{ r: 2.5, fill: "#006837" }} activeDot={{ r: 5 }} />
                      <Line type="monotone" dataKey="disbursed_cr" name="disbursed_cr" stroke="#16A34A" strokeWidth={2} dot={{ r: 2.5, fill: "#16A34A" }} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : <div className="py-8 text-center text-sm text-slate-600">{loading ? "Loading..." : "No quarterly data"}</div>
            )}
          </div>
        </div>

      {/* 2. Source of Fund × Portfolio Heatmap (side-by-side with YoY) */}
      {(trustMatrix.trusts.length > 0 || awpMatrix.trusts.length > 0) && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col" data-testid="trust-theme-heatmap-section">
          <div className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Source of Fund × Portfolio</h3>
              <p className="text-xs text-slate-500 mt-0.5">Grants and sanctioned by trust & theme</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setHeatmapToggle("grants")} data-testid="heatmap-toggle-grants"
                className={`text-[10px] px-2.5 py-1 rounded-full border font-medium transition-colors ${heatmapToggle === "grants" ? "bg-[#006837] text-white border-[#006837]" : "bg-white text-slate-600 border-slate-200 hover:border-[#006837]"}`}>
                Grants
              </button>
              <button onClick={() => setHeatmapToggle("alloc")} data-testid="heatmap-toggle-alloc"
                className={`text-[10px] px-2.5 py-1 rounded-full border font-medium transition-colors ${heatmapToggle === "alloc" ? "bg-[#006837] text-white border-[#006837]" : "bg-white text-slate-600 border-slate-200 hover:border-[#006837]"}`}>
                ₹ Sanctioned
              </button>
            </div>
          </div>
          <div className="p-4 overflow-x-auto flex-1">
            <HeatmapTable matrix={trustMatrix} toggle={heatmapToggle} />
          </div>
        </div>
      )}
      </div>

      {/* ── Sankey: Sanctioned Amount Flow (hidden) ────────────────── */}

      {/* ── Inline Grants Table ─────────────────────────────────────── */}
      <div ref={grantsTableRef} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="exec-grants-table-section">
        <div
          className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-center justify-between cursor-pointer select-none"
          onClick={() => setGrantsTableOpen(!grantsTableOpen)}
          data-testid="exec-grants-table-toggle"
        >
          <div>
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              Grants
              <ChevronDown size={14} className={`text-slate-600 transition-transform ${grantsTableOpen ? "rotate-180" : ""}`} />
            </h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <p className="text-xs text-slate-500">{grantsTotal.toLocaleString()} grants</p>
              {grantsTableOpen && <span className="text-[11px] text-[#006837] font-semibold">Click on Grant ID to view more details</span>}
              {selectedState && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 rounded text-[10px] font-medium" data-testid="grants-state-filter-tag">
                  {selectedState}{selectedDistrict ? ` / ${selectedDistrict}` : ""}
                </span>
              )}
            </div>
          </div>
          {grantsTableOpen && (
            <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
              <form onSubmit={handleGrantsSearch} className="flex items-center gap-1.5">
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-600" />
                  <input
                    type="text"
                    value={grantsSearchInput}
                    onChange={(e) => setGrantsSearchInput(e.target.value)}
                    placeholder="Search grants..."
                    className="pl-8 pr-7 py-1.5 border border-slate-200 rounded-lg text-xs w-56 focus:outline-none focus:ring-2 focus:ring-[#006837]/20 focus:border-[#006837]"
                    data-testid="exec-grants-search"
                  />
                  {grantsSearchInput && (
                    <button type="button" onClick={clearGrantsSearch} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-600">
                      <X size={12} />
                    </button>
                  )}
                </div>
                <button type="submit" className="px-2.5 py-1.5 bg-[#006837] text-white rounded-lg text-xs font-medium hover:bg-[#8B1515]" data-testid="exec-grants-search-btn">Search</button>
              </form>
              <button
                onClick={handleGrantsExport}
                disabled={grantsExporting}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 disabled:opacity-60 transition-colors whitespace-nowrap"
                data-testid="exec-grants-export"
              >
                <Download size={13} />
                {grantsExporting ? "Exporting..." : "Export CSV"}
              </button>
            </div>
          )}
        </div>

        {grantsTableOpen && (
          <>
            {grantsLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#006837]" />
              </div>
            ) : grants.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-slate-600">
                <FileText size={40} className="mb-2 opacity-40" />
                <p className="text-sm">No grants found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="exec-grants-table">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <SortTh label="Grant ID"     field="grant_id"           sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} className="px-3" />
                      <SortTh label="Project Title" field="project_title"     sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="Grantee"      field="grantee_name"       sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="Portfolio"    field="theme"              sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="Status"       field="status"             sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="Sanctioned"   field="amount_recommended" sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} className="text-right" />
                      <SortTh label="Disbursed"    field="disbursed"          sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} className="text-right" />
                      {selectedState && (
                        <>
                          <th className="text-left font-medium text-amber-600 uppercase py-2 px-2 whitespace-nowrap text-[10px] bg-amber-50/50" data-testid="col-sanctioned-geo">Sanctioned ({selectedDistrict || selectedState})</th>
                          <th className="text-left font-medium text-amber-600 uppercase py-2 px-2 whitespace-nowrap text-[10px] bg-amber-50/50" data-testid="col-disbursed-geo">Disbursed ({selectedDistrict || selectedState})</th>
                        </>
                      )}
                      <SortTh label="Start Date"   field="project_start_date" sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="End Date"     field="project_end_date"   sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="FY"           field="fy"                 sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="Grant Approved Date" field="grant_approved_date" sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                    </tr>
                  </thead>
                  <tbody>
                    {grants.map((g, i) => (
                      <tr key={g.grant_id || i} className={`border-b border-slate-50 hover:bg-slate-50 transition-colors ${i % 2 === 0 ? "bg-white" : "bg-slate-50/30"}`}>
                        <td className="py-2 px-3 font-mono whitespace-nowrap">
                          <button onClick={() => navigate(`/grant-overview?grant=${g.grant_id}`)}
                            className="text-[#006837] hover:underline cursor-pointer font-medium">{g.grant_id || "–"}</button>
                        </td>
                        <td className="py-2 px-2 whitespace-nowrap">
                          <span className="font-medium text-slate-800 max-w-[200px] truncate block" title={g.project_title}>{g.project_title || "–"}</span>
                        </td>
                        <td className="py-2 px-2 whitespace-nowrap">
                          <span className="text-slate-700 max-w-[140px] truncate block" title={g.grantee}>{g.grantee || "–"}</span>
                        </td>
                        <td className="py-2 px-2 whitespace-nowrap">{g.theme ? <ThemeChip theme={g.theme} /> : "–"}</td>
                        <td className="py-2 px-2 whitespace-nowrap">
                          <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${statusClass(g.status)}`}>{g.status || "–"}</span>
                        </td>
                        <td className="py-2 px-2 text-right font-medium text-slate-800 whitespace-nowrap">{g.amount_cr ? fmtCr(g.amount_cr) : "–"}</td>
                        <td className="py-2 px-2 text-right font-medium text-green-700 whitespace-nowrap">{g.disbursed_cr ? fmtCr(g.disbursed_cr) : "–"}</td>
                        {selectedState && (
                          <>
                            <td className="py-2 px-2 text-right font-medium text-amber-700 whitespace-nowrap bg-amber-50/30" data-testid="sanctioned-geo-val">{g.sanctioned_geo_cr != null ? fmtCr(g.sanctioned_geo_cr) : "–"}</td>
                            <td className="py-2 px-2 text-right font-medium text-amber-700 whitespace-nowrap bg-amber-50/30" data-testid="disbursed-geo-val">{g.disbursed_geo_cr != null ? fmtCr(g.disbursed_geo_cr) : "–"}</td>
                          </>
                        )}
                        <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{fmtDate(g.start_date)}</td>
                        <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{fmtDate(g.end_date)}</td>
                        <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{g.fy || "–"}</td>
                        <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{fmtDate(g.approved_date)}</td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        )}
          </>
        )}

        {/* Pagination */}
        {grantsTableOpen && grantsTotalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50">
            <div className="text-xs text-slate-500">
              Showing {((grantsPage - 1) * 50) + 1}–{Math.min(grantsPage * 50, grantsTotal)} of {grantsTotal} grants
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setGrantsPage(p => Math.max(1, p - 1))} disabled={grantsPage === 1}
                className="p-1.5 rounded border border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100" data-testid="exec-grants-prev">
                <ChevronLeft size={14} />
              </button>
              <span className="text-xs text-slate-600">Page {grantsPage} of {grantsTotalPages}</span>
              <button onClick={() => setGrantsPage(p => Math.min(grantsTotalPages, p + 1))} disabled={grantsPage === grantsTotalPages}
                className="p-1.5 rounded border border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100" data-testid="exec-grants-next">
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── SortTh Sub-Component ────────────────────────────────────────── */
function SortTh({ label, field, sortBy, sortOrder, onSort, className = "" }) {
  const active = sortBy === field;
  return (
    <th
      className={`text-left font-medium text-slate-500 uppercase py-2 px-2 whitespace-nowrap cursor-pointer select-none hover:text-slate-800 hover:bg-slate-100 transition-colors group ${className}`}
      onClick={() => onSort(field)}
      data-testid={`exec-sort-${field}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={`transition-colors ${active ? "text-[#006837]" : "text-slate-500 group-hover:text-slate-600"}`}>
          {active
            ? sortOrder === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />
            : <ChevronsUpDown size={12} />}
        </span>
      </span>
    </th>
  );
}

/* ─── Heatmap Sub-Component ──────────────────────────────────────────── */
function HeatmapTable({ matrix, toggle }) {
  if (!matrix.data.length) return null;
  const suffix = toggle === "grants" ? "_grants" : toggle === "awp" ? "_awp" : "_alloc";
  const isCount = toggle === "grants";
  const allVals = matrix.data.flatMap(row =>
    matrix.trusts.map(t => row[`${t}${suffix}`])
  ).filter(Boolean);
  const maxVal = Math.max(...allVals, 1);
  const colTotals = matrix.trusts.map(t =>
    matrix.data.reduce((sum, row) => sum + (row[`${t}${suffix}`] || 0), 0)
  );
  const grandTotal = colTotals.reduce((a, b) => a + b, 0);
  const fmt = v => v > 0 ? (isCount ? v : fmtCrNoSymbol(v)) : "—";

  return (
    <>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr>
            <th className="text-left text-slate-500 font-medium py-2 pr-3 w-32">Portfolio</th>
            {matrix.trusts.map(t => (
              <th key={t} className="text-center text-slate-600 font-semibold py-2 px-1.5 min-w-[64px]">{t}</th>
            ))}
            <th className="text-center text-slate-700 font-bold py-2 px-1.5 min-w-[64px] border-l border-slate-200">Total</th>
          </tr>
        </thead>
        <tbody>
          {matrix.data.map((row, ri) => {
            const rowTotal = matrix.trusts.reduce((sum, t) =>
              sum + (row[`${t}${suffix}`] || 0), 0
            );
            return (
              <tr key={ri}>
                <td className="py-1.5 pr-3 font-medium text-slate-700 whitespace-nowrap text-[11px]">
                  <ThemeChip theme={row.theme} />
                </td>
                {matrix.trusts.map(t => {
                  const val = row[`${t}${suffix}`];
                  const intensity = val > 0 ? Math.max(0.08, val / maxVal) : 0;
                  const display = val > 0 ? (isCount ? val : fmtCrNoSymbol(val)) : "—";
                  return (
                    <td key={t} className="py-1.5 px-1.5 text-center rounded transition-colors"
                      style={{ backgroundColor: val > 0 ? `rgba(0,104,55,${intensity})` : "transparent",
                               color: intensity > 0.55 ? "#fff" : intensity > 0 ? "#006837" : "#CBD5E1" }}>
                      <span className="font-semibold text-[11px]">{display}</span>
                    </td>
                  );
                })}
                <td className="py-1.5 px-1.5 text-center border-l border-slate-200 bg-slate-50">
                  <span className="font-bold text-slate-700 text-[11px]">{fmt(rowTotal)}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-slate-200">
            <td className="py-2 pr-3 font-bold text-slate-700 text-[11px]">Total</td>
            {colTotals.map((total, i) => (
              <td key={i} className="py-2 px-1.5 text-center bg-slate-50">
                <span className="font-bold text-slate-700 text-[11px]">{fmt(total)}</span>
              </td>
            ))}
            <td className="py-2 px-1.5 text-center border-l border-slate-200 bg-slate-100">
              <span className="font-bold text-[#006837] text-[11px]">{fmt(grandTotal)}</span>
            </td>
          </tr>
        </tfoot>
      </table>
      <div className="flex items-center gap-3 mt-3 text-[10px] text-slate-600">
        <span>Intensity:</span>
        {[0.1, 0.3, 0.6, 1].map(o => (
          <span key={o} className="flex items-center gap-1">
            <span className="w-4 h-3 rounded-sm inline-block" style={{ backgroundColor: `rgba(0,104,55,${o})` }} />
            {o === 0.1 ? "Low" : o === 0.3 ? "Med" : o === 0.6 ? "High" : "Max"}
          </span>
        ))}
      </div>
    </>
  );
}
